import { useMemo, useState } from 'react';
import type { ConfirmationDraft } from '../types/agui.types';
import type {
  ApprovalFailure,
  ApproveOptions,
} from '../hooks/use-assistant-chat';
import { confirmationDisplayEntries } from '../helpers/confirmation-display';
import { resolveLanguage, staticLabel, isRtl } from '../helpers/card-labels';
import { readImportBatch } from '../helpers/import-display';
import type { TiaReaction } from './tia/tia-avatar';
import { TiaThreadAvatar } from './tia/tia-thread-avatar';
import { ImportBatchCard } from './import-batch-card';

/**
 * Renderer for the Level-4 confirmation stream event. Purely event-driven:
 * the hook owns the confirmation state; this card only presents the draft
 * and forwards approve/reject (plus the FR-15 micro-reaction, fire-and-forget).
 *
 * Visual (Figma 20324:93290): primary Confirm + outline Cancel. After Confirm,
 * those buttons become a right-aligned user-message bubble saying “Confirm”.
 *
 * Fix round 2, item #4: the card speaks the chat's language. `draft.language`
 * is an optional ISO 639-1 code the backend forwards as the turn's reply
 * language; it may be absent (an older backend, or a draft built before
 * that change), in which case `resolveLanguage` falls back to English and
 * every label below renders exactly as it did before this change.
 *
 * Document import (spec §9): a draft carrying `import` renders the
 * ImportBatchCard instead — one approval for the whole batch, with rows the
 * user can untick. Every other draft renders the key/value card below,
 * unchanged.
 */

interface ConfirmationCardProps {
  draft: ConfirmationDraft;
  /** Import cards pass the unticked rows; every other card passes nothing. */
  onApprove: (options?: ApproveOptions) => void | Promise<void>;
  onReject: () => void | Promise<void>;
  /** FR-15: micro-reaction hint fired before approve ('ack') / reject ('blink'). */
  onReact?: (kind: TiaReaction) => void;
  /** Import cards only: the server refused the last approval (a 400). */
  approvalFailure?: ApprovalFailure | null;
}

export function ConfirmationCard(props: ConfirmationCardProps) {
  const { draft } = props;
  const batch = useMemo(() => readImportBatch(draft.import), [draft.import]);
  if (batch) {
    return (
      <ImportBatchCard
        {...props}
        batch={batch}
      />
    );
  }
  return <KeyValueConfirmationCard {...props} />;
}

function KeyValueConfirmationCard({
  draft,
  onApprove,
  onReject,
  onReact,
}: ConfirmationCardProps) {
  const [busy, setBusy] = useState(false);
  const [cancelFailed, setCancelFailed] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const language = resolveLanguage(draft.language);
  const rtl = isRtl(language);

  const summary = typeof draft.summary === 'string' ? draft.summary : undefined;
  const title = summary ?? staticLabel(language, 'waitingTitle');
  // The pause payload's args are near-raw tool args — only the allowlisted
  // presentational fields render (internal ids/machine keys never show).
  // `arguments` is what the gateway's pause payload actually calls it (PendingAction
  // .draft). Reading `draft.args` silently produced an empty list, so every write was
  // confirmed with no detail at all beyond "the assistant wants to perform a write".
  // Fix round 3, task 2: `draft.argument_labels` (OPTIONAL, PATH-keyed —
  // task-2-brief.md) swaps a row's raw id for the backend's resolved name.
  // No live draft sends it yet; an absent key is a no-op through this helper.
  const args = confirmationDisplayEntries(
    draft.arguments,
    language,
    draft.argument_labels
  );

  const approve = async () => {
    onReact?.('ack'); // fire-and-forget: the avatar acks while the resume runs
    setConfirmed(true);
    setBusy(true);
    try {
      await onApprove();
    } catch {
      setConfirmed(false);
    } finally {
      setBusy(false);
    }
  };

  // Cancel only clears the card once the backend has actually cancelled the pending action.
  // If it fails, keep the card and surface an error so the user can retry — silently clearing
  // would leave a dangling pending action server-side that blocks re-issuing the same write.
  const cancel = async () => {
    onReact?.('blink');
    setBusy(true);
    setCancelFailed(false);
    try {
      await onReject();
    } catch {
      setCancelFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-slot='confirmation-card'
      className='flex w-full flex-col gap-2'
      dir={rtl ? 'rtl' : undefined}
    >
      <div className='flex flex-col gap-1'>
        <div className='flex items-center gap-1'>
          <TiaThreadAvatar />
          <p className='text-base text-secondary-1000'>{title}</p>
        </div>
        {args.length > 0 && (
          <dl
            data-slot='confirmation-kv'
            className='flex w-full flex-col gap-2 rounded-2xl border border-primary-200 bg-primary-50 p-4'
          >
            {args.map(([k, v]) => (
              <div
                key={k}
                className='flex items-center justify-between gap-3 text-sm font-medium tracking-wide text-gray-800'
              >
                <dt className='shrink-0'>{k}</dt>
                {/* Fix round 1: an LTR value (email, phone, ISO date) inside
                 * an RTL card gets bidi-reordered unless it carries its own
                 * direction — dir="auto" lets each value read correctly
                 * regardless of the card's own dir. */}
                <dd
                  className='min-w-0 break-words text-right'
                  dir='auto'
                >
                  {v}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      {cancelFailed && (
        <p className='text-xs text-red-600'>
          {staticLabel(language, 'cancelFailed')}
        </p>
      )}

      {confirmed ? (
        <div className='flex justify-end'>
          <div
            data-slot='confirmation-ack'
            className='rounded-tl-3xl rounded-tr-3xl rounded-bl-3xl rounded-br-none bg-primary-100 px-3 py-2 text-base text-primary-1000'
          >
            {staticLabel(language, 'confirmedBubble')}
          </div>
        </div>
      ) : (
        <div className='flex items-center gap-2'>
          <button
            type='button'
            disabled={busy}
            onClick={approve}
            className='inline-flex h-12 items-center justify-center rounded-xl bg-primary-1000 px-5 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50'
          >
            {staticLabel(language, 'confirm')}
          </button>
          <button
            type='button'
            disabled={busy}
            onClick={cancel}
            className='inline-flex h-12 items-center justify-center rounded-xl border border-primary-1000 bg-transparent px-5 py-3 text-sm font-medium text-primary-1000 transition-opacity hover:opacity-90 disabled:opacity-50'
          >
            {staticLabel(language, 'cancel')}
          </button>
        </div>
      )}
    </div>
  );
}
